import { openRequestStates, stateWord } from "@lafayette/domain";
import type {
  RequestConflict,
  StaffRequestPriorityValue,
  StaffRequestQueueFilterValue,
  StaffRequestStateValue,
  StaffRequestWorkStatusValue,
} from "@lafayette/schemas";

import type { Look } from "../ui/status-look";

// The words of the Requests UI. A state or a flag always has a glyph AND a word (the look maps give the glyph and the tone), never a colour alone. The word of a
// request state comes from `stateWord` of `@lafayette/domain` (CHT-1990): this file keeps no word table for a state. Closed lists repeat the schema tuples; a
// `Record` over the schema type makes the compiler fail when a value is added there and not here.

/** The tone and the glyph of a status. The word comes from `stateWord("request", "staffShort", state)`. */
export type { Look };

export type Option = Readonly<{ value: string; label: string }>;

/** How a state looks: a tone and a glyph (always drawn with its word by `StatusBadge`). */
export const stateLook: Readonly<Record<StaffRequestStateValue, Look>> = {
  DRAFT: { tone: "neutral", icon: "circle" },
  SUBMITTED: { tone: "info", icon: "inbox" },
  ASSIGNED: { tone: "info", icon: "user-check-01" },
  IN_PROGRESS: { tone: "info", icon: "play-circle" },
  AWAITING_CUSTOMER: { tone: "warning", icon: "pause" },
  PAYMENT_REQUIRED: { tone: "brass", icon: "credit-card" },
  CONFIRMED: { tone: "success", icon: "checkmark-circle-02" },
  COMPLETED: { tone: "success", icon: "checkmark-badge-01" },
  CANCELLED: { tone: "neutral", icon: "cancel-circle" },
  UNAVAILABLE: { tone: "neutral", icon: "minus-sign-circle" },
  DECLINED: { tone: "neutral", icon: "unavailable" },
  EXPIRED: { tone: "neutral", icon: "hourglass-off" },
};

/** The states in which a request is open: staff can still act on it (`openRequestStates` of `@lafayette/domain`). */
const OPEN_STATES: readonly StaffRequestStateValue[] = openRequestStates;

export const isOpenState = (state: StaffRequestStateValue): boolean => OPEN_STATES.includes(state);

const typeLabels: Readonly<Record<string, string>> = {
  DINING: "Dining",
  VILLA: "Villa",
  EVENT: "Event",
  EXPERIENCE: "Experience",
  SERVICE: "Service",
  TRANSPORT: "Transport",
  OTHER: "Other",
};
export const typeLabel = (type: string): string => typeLabels[type] ?? type;

export const priorityLabels: Readonly<Record<StaffRequestPriorityValue, string>> = {
  STANDARD: "Standard",
  HIGH: "High",
  URGENT: "Urgent",
};
export const priorityLook: Readonly<Record<StaffRequestPriorityValue, Look>> = {
  STANDARD: { tone: "neutral", icon: "minus-sign" },
  HIGH: { tone: "warning", icon: "arrow-up-01-round" },
  URGENT: { tone: "danger", icon: "arrow-up-double-round" },
};
export const priorityOptions: readonly Option[] = (
  Object.keys(priorityLabels) as StaffRequestPriorityValue[]
).map((value) => ({ value, label: priorityLabels[value] }));

export const workStatusLabels: Readonly<Record<StaffRequestWorkStatusValue, string>> = {
  NONE: "No progress marker",
  WITH_SUPPLIER: "With the supplier",
  INTERNAL_REVIEW: "In internal review",
};
export const workStatusOptions: readonly Option[] = (
  Object.keys(workStatusLabels) as StaffRequestWorkStatusValue[]
).map((value) => ({ value, label: workStatusLabels[value] }));

// A filter that is a state takes the staff word of that state from `@lafayette/domain`; the other filters name a queue, not a state.
const staffState = (state: StaffRequestStateValue): string =>
  stateWord("request", "staffShort", state);

export const filterLabels: Readonly<Record<StaffRequestQueueFilterValue, string>> = {
  ASSIGNED_TO_ME: "Mine",
  UNASSIGNED: "Unassigned",
  SUBMITTED: staffState("SUBMITTED"),
  IN_PROGRESS: staffState("IN_PROGRESS"),
  AWAITING_CUSTOMER: staffState("AWAITING_CUSTOMER"),
  AWAITING_SUPPLIER: "Waiting for supplier",
  PAYMENT_REQUIRED: staffState("PAYMENT_REQUIRED"),
  CONFIRMED: staffState("CONFIRMED"),
  COMPLETED: staffState("COMPLETED"),
  UNAVAILABLE_OR_CANCELLED: "Unavailable or cancelled",
  AGING: "Aging",
};

export const filterDescriptions: Readonly<Record<StaffRequestQueueFilterValue, string>> = {
  ASSIGNED_TO_ME: "Assigned to you, or open and owned by you.",
  UNASSIGNED: "Submitted with no named owner. A coverage gap: a supervisor assigns it.",
  SUBMITTED: "Submitted and not yet worked.",
  IN_PROGRESS: "A concierge is working on it.",
  AWAITING_CUSTOMER: "A question to the customer is open.",
  AWAITING_SUPPLIER: "In progress and marked as waiting for the supplier.",
  PAYMENT_REQUIRED: "A payment is needed to continue.",
  CONFIRMED: "Confirmed on evidence. Not yet completed.",
  COMPLETED: "Completed. Newest first.",
  UNAVAILABLE_OR_CANCELLED: "Ended without being fulfilled. Newest first.",
  AGING:
    "Open longer than the configured aging threshold. Empty when no threshold is set on the server.",
};

export const flagLabels = {
  UNASSIGNED: "No owner",
  // A flag is not a state: the detail page draws the state badge AND the flag, so the two must not read the same.
  AWAITING_CUSTOMER: "Waiting for customer",
  AGING: "Aging",
} as const;
export const flagLook = {
  UNASSIGNED: { tone: "warning", icon: "alert-02" },
  AWAITING_CUSTOMER: { tone: "info", icon: "pause" },
  AGING: { tone: "warning", icon: "timer-01" },
} as const satisfies Record<string, Look>;

export const COVERAGE_GAP_LABEL = "Coverage gap: no owner";
export const COVERAGE_GAP_DETAIL =
  "Routing found nobody to hold this request. A supervisor must assign it.";

const routeKindLabels: Readonly<Record<string, string>> = {
  TEMPORARY: "Routed to the temporary cover",
  PRIMARY: "Routed to the primary concierge",
  BACKUP: "Routed to the backup concierge",
  SUPERVISOR_QUEUE: "Routed to the supervisor queue",
};
const ROUTED_BY_SERVICE = "Routed by the routing service";

/**
 * The reference of an assignment row in words. The domain writes `ROUTE:<routing kind>:<staff id, or QUEUE>` for a routing decision and
 * `DIRECT:<staff id>` for a supervisor's choice. A staff id is never shown: the row already names who got the request. Any other reference
 * (a supplier or response reference a person wrote) is shown as written.
 */
export function evidenceReferenceLabel(reference: string): string {
  if (reference.startsWith("ROUTE:")) {
    return routeKindLabels[reference.split(":")[1] ?? ""] ?? ROUTED_BY_SERVICE;
  }
  if (reference.startsWith("DIRECT:")) return "Named by a supervisor";
  return reference;
}

/** The reason the routing service writes (`assignment:<routing kind>`) in words; a reason a person wrote is shown as written. */
export function internalReasonLabel(reason: string): string {
  const route = /^assignment:([a-z_]+)$/u.exec(reason);
  if (route === null) return reason;
  return routeKindLabels[(route[1] as string).toUpperCase()] ?? ROUTED_BY_SERVICE;
}

export const assignmentSourceLabels: Readonly<Record<string, string>> = {
  TEMPORARY: "Temporary cover",
  PRIMARY: "Primary concierge",
  BACKUP: "Backup concierge",
  SUPERVISOR_QUEUE: "Supervisor queue",
  DIRECT: "Named by a person",
  ROUTE: "By routing",
};

export const noteKindLabels: Readonly<Record<string, string>> = {
  SUPPLIER_CONTACT: "Supplier contact",
  SUPPLIER_QUOTE_CONTEXT: "Supplier quote context",
  STAFF_NOTE: "Staff note",
  HANDOVER: "Handover",
};
export const noteKindOptions: readonly Option[] = Object.entries(noteKindLabels).map(
  ([value, label]) => ({ value, label }),
);

const completionEvidenceLabels: Readonly<Record<string, string>> = {
  SERVICE_DELIVERED: "The service was delivered",
  CUSTOMER_ACKNOWLEDGED: "The customer acknowledged it",
  SUPPLIER_REPORTED_FULFILMENT: "The supplier reported fulfilment",
};
export const completionEvidenceOptions: readonly Option[] = Object.entries(
  completionEvidenceLabels,
).map(([value, label]) => ({ value, label }));

export const evidenceKindLabels: Readonly<Record<string, string>> = {
  ASSIGNMENT_DECISION: "Assignment decision",
  CUSTOMER_RESPONSE: "Customer response",
  STAFF_RECORDED_RESPONSE: "Recorded response",
  PAYMENT_REQUEST: "Payment request",
  PAYMENT_OUTCOME: "Payment outcome",
  PAYMENT_REPLAN: "Payment re-plan",
  SUPPLIER_CONFIRMATION: "Supplier confirmation",
  OPERATIONAL_CLOSURE: "Operational closure",
  EXPIRY_POLICY: "Expiry policy",
  ...completionEvidenceLabels,
};

export const fieldTypeLabels = {
  TEXT: "Text",
  NUMBER: "Number",
  DATE: "Date",
  TIME: "Time",
  CHOICE: "Choice",
  BOOLEAN: "Yes or no",
} as const;
export const fieldTypeOptions: readonly Option[] = Object.entries(fieldTypeLabels).map(
  ([value, label]) => ({ value, label }),
);

export const questionStatusLabels: Readonly<Record<string, string>> = {
  OPEN: "Open",
  ANSWERED: "Answered",
  WITHDRAWN: "Withdrawn",
};
export const answerChannelLabels: Readonly<Record<string, string>> = {
  ACTION_CENTER: "In the app form",
  CHAT: "In chat",
  STAFF_RECORDED: "Recorded by staff",
};

export const conflictCodeLabels: Readonly<Record<RequestConflict["code"], string>> = {
  "request.version_conflict": "The request changed after you opened it.",
  "request.transition_invalid": "This action is not possible in the state the request is in now.",
  "request.terminal": "The request has already ended.",
  "request.same_owner": "That person already holds the request.",
  "request.assignee_ineligible":
    "That person cannot take this request. They need an active assignment to the customer.",
  "request.payment_request_ineligible": "A payment cannot be requested for this request.",
  "request.payment_outcome_pending": "A payment outcome is still pending.",
  "request.payment_already_settled": "The payment is already settled.",
  "request.not_expired_yet": "The request has not expired yet.",
  "request.no_eligible_assignee": "Nobody is eligible to take this request.",
};

// --- fulfilment evidence, customer updates and attachments (CHT-752) --------------------------------------------------------------

export const fulfilmentKindLabels: Readonly<Record<string, string>> = {
  SUPPLIER_CONTACT: "Supplier contact",
  AVAILABILITY: "Availability",
  QUOTE: "Quote",
  CONFIRMATION_REFERENCE: "Confirmation reference",
  UNAVAILABLE_REASON: "Unavailable reason",
  CORRECTION: "Correction",
};
/** The kinds a staff member records by hand. A correction has its own button, and the unavailable reason its own command. */
const recordableEvidenceKinds = [
  "SUPPLIER_CONTACT",
  "AVAILABILITY",
  "QUOTE",
  "CONFIRMATION_REFERENCE",
] as const;
export const evidenceKindOptions: readonly Option[] = recordableEvidenceKinds.map((value) => ({
  value,
  label: fulfilmentKindLabels[value] ?? value,
}));

export const contactMethodLabels: Readonly<Record<string, string>> = {
  PHONE: "Phone",
  EMAIL: "Email",
  WHATSAPP: "WhatsApp",
  IN_PERSON: "In person",
  OTHER: "Other",
};
export const contactMethodOptions: readonly Option[] = Object.entries(contactMethodLabels).map(
  ([value, label]) => ({ value, label }),
);

export const confirmationKindLabels: Readonly<Record<string, string>> = {
  SUPPLIER_WRITTEN_CONFIRMATION: "Written confirmation from the supplier",
  SUPPLIER_VERBAL_CONFIRMATION: "Verbal confirmation from the supplier",
  LAFAYETTE_DIRECT_FULFILMENT: "Lafayette fulfils it directly",
};
export const confirmationKindOptions: readonly Option[] = Object.entries(
  confirmationKindLabels,
).map(([value, label]) => ({ value, label }));

export const updateKindLabels: Readonly<Record<string, string>> = {
  NEXT_STEP: "Next step",
  CONFIRMATION_DETAILS: "Confirmation details",
  ALTERNATIVES: "Alternatives",
  UNAVAILABLE_NOTE: "Why it was not available",
  RESTRICTIONS: "Restrictions",
};
export const attachmentTypeLabels: Readonly<Record<string, string>> = {
  "application/pdf": "PDF",
  "image/jpeg": "JPEG image",
  "image/png": "PNG image",
  "image/webp": "WebP image",
};
/** CHT-753. What needs a person now, in plain words. A chip always has a glyph AND these words. */
export const attentionLabels = {
  REQUEST_SUBMITTED: "New",
  REQUEST_UNASSIGNED: "Unassigned",
  REQUEST_AGING: "Aging",
} as const;
export const attentionLook = {
  REQUEST_SUBMITTED: { tone: "info", icon: "star" },
  REQUEST_CUSTOMER_RESPONDED: { tone: "info", icon: "arrow-turn-backward-round" },
  REQUEST_UNASSIGNED: { tone: "warning", icon: "alert-02" },
  REQUEST_AGING: { tone: "warning", icon: "timer-01" },
} as const satisfies Record<string, Look>;
